import React, { useEffect, useRef, useState } from "react";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { MapPanelTabContent } from "./MapPanelTabContent";
import SearchTextbox from "../../SearchTextbox";
import { isArrayEmpty, isNullOrEmpty } from "../../../helpers/ObjectHelpers";
import MapApiRepository from "../repositories/MapApiRepository";
import { IMapParamsService } from "../services/MapParamsService";
import { useSetSelectLocation, useSetSelectShipPosition } from "../hooks/useMapStore";

const initialState = {
    searchText: "",
    selectedTab: 0,
    locations: [],
    selectedLocation: null,
    ships: [],
    selectedShip: null,
    isBusy: false
};

interface MapPanelSearchProps {
    panelTabContentHeight: number,
    shouldCloseOnClick: boolean,
    onClose: () => void,
    paramsService: IMapParamsService,
}

export const MapPanelSearch = (
    {
        panelTabContentHeight,
        shouldCloseOnClick,
        onClose,
        paramsService,
    }: MapPanelSearchProps) => {

    const setSelectLocation = useSetSelectLocation();
    const setSelectShipPosition = useSetSelectShipPosition();

    const timer: { current: NodeJS.Timeout | null } = useRef(null); 
    const searchControlHeight = 50;
    let abortController = new AbortController();

    const [{
        searchText,
        selectedTab,
        locations,
        selectedLocation,
        ships,
        selectedShip,
        isBusy
    }, setState] = useState(initialState);

    const setStateValue = (property:string, e:boolean | string | number | any[]) => {
        setState((prev) => ({
            ...prev,
            [property]: e
        }));
    }

    const setIsBusy = (e:boolean) => {
        setStateValue("isBusy", e);
    }

    const setSearchText = (e:string) => {
        setStateValue("searchText", e);
    }

    const setSelectedTab = (e:number) => {
        setStateValue("selectedTab", e);
    }

    const setLocations = (e:any[]) => {
        setStateValue("locations", e);
    }

    const setSelectedLocation = (e:any) => {
        setState((prev) => ({
            ...prev,
            selectedLocation: e,
            selectedShip: null
        }));
        setSelectLocation(e);
    }

    const setShips = (e: any[]) => {
        setStateValue("ships", e);
    }

    const setSelectedShip = (e:any) => {
        setState((prev) => ({
            ...prev,
            selectedLocation: null,
            selectedShip: e
        }));
        setSelectShipPosition(e);
    }

    const handleSelectTab = (e:any) => {
        setSelectedTab(e.selected); 
    };

    useEffect(() => {
        searchAsync(paramsService.searchText);
    }, []);

    return (
        <div className={`google-map-search`}>
            <div>
                <SearchTextbox value={searchText} onChange={onSearchChange} autoFocus={true} disabled={isBusy} />
            </div>
            <div>
                <TabStrip selected={selectedTab} onSelect={handleSelectTab}>
                    {
                        (!isArrayEmpty(locations)) &&
                        <TabStripTab title={`Lokasjoner ${getDataCount(locations)}`}>
                            <MapPanelTabContent panelTabContentHeight={panelTabContentHeight - searchControlHeight}>
                                <div className="google-map-search-result">
                                    {
                                        locations.map((location:any, index:number) =>
                                            <MapSearchResultItem
                                                key={index}
                                                name={location.name}
                                                onClick={() => onLocationClick(location)}
                                                selected={location === selectedLocation}
                                            />
                                        )
                                    }
                                </div>
                            </MapPanelTabContent>
                        </TabStripTab>
                    }
                    
                    {
                        (!isArrayEmpty(ships)) &&
                            <TabStripTab title={`Fartøy ${getDataCount(ships)}`}>
                                <MapPanelTabContent panelTabContentHeight={panelTabContentHeight - searchControlHeight}>
                                    <div className="google-map-search-result">
                                        {
                                            ships.map((ship:any, index:number) =>
                                                <MapSearchResultItem
                                                    key={index}
                                                    name={`${ship.shipName} (${ship.callSign})`}
                                                    onClick={() => onShipClick(ship)}
                                                    selected={ship === selectedShip}
                                                />
                                            )
                                        }
                                    </div>
                                </MapPanelTabContent>
                            </TabStripTab>
                        
                    }
                    
                </TabStrip>
            </div>
        </div>
    )

    async function onSearchChange(e:any) {
        if (isBusy) return;

        setSearchText(e.value);

        clearTimeout(timer.current as NodeJS.Timeout); 

        timer.current = setTimeout(async () => {
            await searchAsync(e.value);
            paramsService.onSearchTextChange(e.value);

        }, 500);
    }

    async function searchAsync(searchText: string | null) {
        if (!searchText || isNullOrEmpty(searchText) || searchText.length < 3) {
            setLocations([]);
            setShips([]);
            return;
        }

        setIsBusy(true);

        await searchLocationsAsync(searchText);
        await searchShipsAsync(searchText);

        setIsBusy(false);
    }

    async function searchLocationsAsync(searchText:string) {
        const locations = await MapApiRepository.searchLocationsAsync(searchText);
        setLocations(locations);
    }

    async function searchShipsAsync(searchText: string) {

        abortController.abort();
        abortController = new AbortController();

        const response: any = await MapApiRepository.getShipPositionsByNameAsync(searchText, abortController.signal);
        if (response.ok) {
            const data = await response.json();
            setShips(data);
        }
    }

    function getDataCount(data:any[]) {
        const count = data.length;
        if (data.length === 0) return "";
        return `(${count})`
    } 

    function onLocationClick(location: any) {
        setSelectShipPosition(null);
        setSelectLocation(location);
        //pubSubService.onSelectShipPosition(null);
        //pubSubService.onSelectLocation(location)

        //onUnselectShip()
        //const position = new window.google.maps.LatLng(location.latitude, location.longitude);
        //setSearchParams({
        //    locationId: location.locationId,
        //}, { replace: true });

        //setCenter(position);
        setSelectedLocation(location);
        handleOnClose();
    }

    function onShipClick(ship:any) {
        //const position = new window.google.maps.LatLng(ship.latitude, ship.longitude);
        //setSearchParams({
        //    mmsi: ship.mmsi,
        //}, { replace: true });

        //setCenter(position, 16);
        setSelectedShip(ship);
        handleOnClose();
    }

    //function setCenter(position: google.maps.LatLng, zoom: number = 11) {
    //    if (!map) return;
    //    map.setCenter(position);
    //    map.setZoom(zoom);

        
    //}

    function handleOnClose() {
        if (!shouldCloseOnClick) return;
        onClose();
    }
}


interface MapSearchResultItemProps {
    name: string,
    description?: string,
    onClick: () => void,
    selected: boolean
}

const MapSearchResultItem = (
    {
        name,
        description = "",
        onClick,
        selected = false
    }: MapSearchResultItemProps) => {
    return (
        <div className={`${selected ? 'google-map-search-result-item-selected' : 'google-map-search-result-item'}`} onClick={onClick}>
            <div className="prevent-selection">{name}</div>
            {
                (!isNullOrEmpty(description)) &&
                <div className="prevent-selection">{description}</div>
            }
        </div>
    )
}
